import { Mutex } from 'async-mutex';

import type {
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query';
import type { ITokenResponse } from 'services/user/user.types';

const REFRESH_LOCK_NAME = 'teamhero.token_refresh';

export type TWithRefreshLock = <T>(callback: () => Promise<T>) => Promise<T>;

export interface ITokenRefreshStorage {
  getToken: () => string | null;
  getRefreshToken: () => string | null;
  storeTokens: (token: string, refreshToken: string) => void;
}

export interface IRefreshCallResult {
  data?: unknown;
  error?: FetchBaseQueryError;
  meta?: FetchBaseQueryMeta;
}

export type TRefreshCall = (
  refreshToken: string
) => Promise<IRefreshCallResult>;

/**
 * what a request that got a 401 should do next:
 * - retry: the tokens in storage are valid (replaced by someone else, or just refreshed)
 * - logout: the session is over (rejected refresh, or ended in another tab)
 * - unreachable: the refresh got no verdict, the session stays and the error is returned
 */
export type TUnauthorizedRecovery =
  | { type: 'retry' }
  | { type: 'logout' }
  | {
      type: 'unreachable';
      error: FetchBaseQueryError;
      meta?: FetchBaseQueryMeta;
    };

const RETRY: TUnauthorizedRecovery = { type: 'retry' };
const LOGOUT: TUnauthorizedRecovery = { type: 'logout' };

/**
 * lock shared by all tabs of the origin, so only one of them refreshes at a
 * time. Older WebViews (iOS < 15.4) have no navigator.locks, there the lock
 * only covers this tab, which is enough for the single WebView of a native app
 */
export const createWithRefreshLock = (): TWithRefreshLock => {
  const fallbackLock = new Mutex();

  return (callback) => {
    if (typeof navigator !== 'undefined' && 'locks' in navigator) {
      return navigator.locks.request(REFRESH_LOCK_NAME, callback);
    }
    return fallbackLock.runExclusive(callback);
  };
};

/**
 * reads the access token a request was actually sent with
 */
export const getSentToken = (meta?: FetchBaseQueryMeta): string | null => {
  const authorization = meta?.request.headers.get('authorization');
  return authorization?.replace(/^Bearer /, '') ?? null;
};

export const createTokenRefresher = ({
  withRefreshLock,
  storage,
}: {
  withRefreshLock: TWithRefreshLock;
  storage: ITokenRefreshStorage;
}) => {
  // requests of this tab wait while it recovers from a 401, so a polling burst
  // doesn't send a token that is about to be replaced
  const tabGate = new Mutex();

  const waitForRefresh = (): Promise<void> => tabGate.waitForUnlock();

  const refreshUnderLock = async (
    sentToken: string | null,
    refresh: TRefreshCall
  ): Promise<TUnauthorizedRecovery> => {
    const currentToken = storage.getToken();
    // the session was ended in another tab
    if (!currentToken) {
      return LOGOUT;
    }
    // another request or tab already refreshed, the 401 is stale
    if (currentToken !== sentToken) {
      return RETRY;
    }

    const refreshToken = storage.getRefreshToken();
    if (!refreshToken) {
      return LOGOUT;
    }

    const refreshResult = await refresh(refreshToken);
    if (refreshResult.error?.status === 401) {
      return LOGOUT;
    }
    if (refreshResult.error) {
      return {
        type: 'unreachable',
        error: refreshResult.error,
        meta: refreshResult.meta,
      };
    }

    const { token, refresh_token: newRefreshToken } = (refreshResult.data ??
      {}) as ITokenResponse;
    if (!token || !newRefreshToken) {
      return {
        type: 'unreachable',
        error: {
          status: 'CUSTOM_ERROR',
          error: 'Token refresh returned no tokens',
        },
        meta: refreshResult.meta,
      };
    }

    // storage may have changed while the refresh was in flight. A logout must
    // not be undone by writing the new tokens back
    const tokenAfterRefresh = storage.getToken();
    if (!tokenAfterRefresh) {
      return LOGOUT;
    }
    // only possible without a shared lock: another tab refreshed meanwhile
    if (tokenAfterRefresh !== currentToken) {
      return RETRY;
    }

    storage.storeTokens(token, newRefreshToken);
    return RETRY;
  };

  const recoverFromUnauthorized = (
    sentToken: string | null,
    refresh: TRefreshCall
  ): Promise<TUnauthorizedRecovery> =>
    tabGate.runExclusive(() =>
      withRefreshLock(() => refreshUnderLock(sentToken, refresh))
    );

  return { waitForRefresh, recoverFromUnauthorized };
};
